import { createEffect, untrack } from "solid-js"
import { Group, Mesh, type Object3D } from "three"
import type { Engine } from "../../core/engine/Engine.type"
import type { EntityGraphNode } from "../../core/engine/EntityGraphNode/EntityGraphNode.type"
import applyEntityRotationToNextObject3DFrame from "../../core/entity/EntityHeading/applyEntityRotationToNextObject3DFrame"
import applyEntityWalkPathToNextObject3DFrame from "../../core/entity/EntityHeading/applyEntityWalkPathToNextObject3DFrame"
import type { TransportCopterEntity } from "../../core/entity/EntityType/TransportCopter"
import shouldEntityShowFadedFrames from "../../core/entity/shouldEntityShowFadedFrames"
import tmpRng from "../../core/rng/tmpRng"
import { sin } from "../../core/util/math/Math"
import { mountOnFrameUpdateToObject3D } from "../../event/GameObject3D/GameObject3DFrameUpdate/GameObject3DFrameUpdate"
import assertClassInDev from "../../ldsh/assertClassInDev"
import { CH47Chinook1_CH47Chinook1_GLTF_Model } from "../gltf_models.generated"
import { appGltfLoader } from "./appGltfLoader"
import applyPlayerColors from "./applyPlayerColors"
import creaseGeometryNormalsByMeshNameList from "./creaseGeometryNormalsByMeshNameList"
import runWithObject3DOwner from "./runWithObject3DOwner"

const meshesToCrease = [
  'Body',
  'Body_1',
  'Body_2',
  // 'Rotor',
  // 'Rotor2',
]

export default function makeTransportCopterScene(
  engine: Engine,
  ent: EntityGraphNode<TransportCopterEntity>,
  callback: (newGroup: Object3D) => void
): void {
  return appGltfLoader.load(CH47Chinook1_CH47Chinook1_GLTF_Model, function (ModelObj) {
    tmpRng.seed = ent.id
    const { scene } = ModelObj

    // printObjectHierarchy(scene)

    const bodyGroup = scene.getObjectByName('Body') as Group
    assertClassInDev(bodyGroup, Group)

    const frontRotorGroup = scene.getObjectByName('Rotor1') as Mesh
    assertClassInDev(frontRotorGroup, Mesh)
    const rearRotorGroup = frontRotorGroup.clone()
    rearRotorGroup.name = 'Rotor2'
    rearRotorGroup.position.y += 1.5
    rearRotorGroup.position.z -= 19.5
    bodyGroup.add(rearRotorGroup)

    applyPlayerColors(engine, scene, ent)

    scene.position.y += 6
    const defaultGroupY = bodyGroup.position.y
    // const defaultHullX = 0

    let entValue = untrack(ent.raw)
    runWithObject3DOwner(scene, () => {
      createEffect(() => {
        entValue = ent.raw()
      })
    })

    mountOnFrameUpdateToObject3D(engine, scene, (elapsedMs: number, frameAt: number): void => {
      // const mod = frameAt % totalLoopMs
      if (shouldEntityShowFadedFrames(entValue)) {
        bodyGroup.position.y = defaultGroupY
      } else if (entValue.player_id) {
        bodyGroup.position.y = defaultGroupY + ((sin(frameAt / 1000)) + 1) / 2
        frontRotorGroup.rotation.y = (frameAt / 120)
        rearRotorGroup.rotation.y = (-frameAt / 120)
      }

      applyEntityRotationToNextObject3DFrame(scene, elapsedMs)
      applyEntityWalkPathToNextObject3DFrame(scene, elapsedMs)
    })

    // console.log(entityDisplayName(entValue))
    // console.log(printObjectHierarchy(scene))
    creaseGeometryNormalsByMeshNameList(scene, meshesToCrease)

    callback(scene)
  })
}

